import { Building2, Clock, Info } from 'lucide-react'

import type { Branch } from '@/api/types'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { branchDescription, branchLabel } from '@/lib/branch'
import { formatDateTime } from '@/lib/datetime'

import type { LedgerBranchReach } from './queries'

/**
 * Which branches a ledger screen may act on, and the two things that are wrong with that list.
 *
 * **It is a floor, not a set.** `/access/me/branches` builds `items` from the user's home branch
 * plus their explicit grants. A head-office accountant holding `admin.access.all_branches` may
 * operate anywhere the server will answer for, and still sees one entry here, because no module
 * owns branches as records and there is nothing to enumerate the rest from. A screen that renders
 * "a card per accessible branch" and stops would let that person read one branch as the whole
 * institution — so the shortfall is stated rather than left to be discovered.
 *
 * **The labels are missing.** `directoryAvailable` is false for the same reason, which is what
 * makes every code and name null. The identifier is then the only true thing available, and it is
 * what gets shown: a bare "Branch" names nothing and an invented label is a name the institution
 * never gave.
 *
 * A time-boxed grant is the third fact, and it is per-branch rather than global: cover
 * arrangements expire, the server resolves the furthest live expiry, and a branch whose grant
 * lapses mid-session keeps appearing in a list that was fetched before it lapsed.
 */
export function BranchReachNotice({ reach }: { reach: LedgerBranchReach }) {
  if (reach.isPending || reach.isError) {
    return null
  }

  const expiring = reach.branches.filter((branch) => Boolean(branch.accessExpiresAtUtc))

  return (
    <div className="space-y-2">
      {reach.canAccessAllBranches ? (
        <Alert tone="warning">
          <Info aria-hidden />
          <div className="space-y-1">
            <AlertTitle>This is not every branch</AlertTitle>
            <AlertDescription>
              You hold all-branch access, so the server will answer for branches that are not listed
              here. The list is built from your home branch plus the grants recorded against you —
              there is no branch registry to enumerate the rest from, so treat{' '}
              {reach.branches.length === 1 ? 'this one card' : `these ${reach.branches.length} cards`} as
              a floor rather than as the institution. A branch that is missing cannot be selected
              on this screen at all.
            </AlertDescription>
          </div>
        </Alert>
      ) : null}

      {!reach.directoryAvailable ? (
        <Alert tone="info">
          <Building2 aria-hidden />
          <div className="space-y-1">
            <AlertTitle>Branches are shown by identifier</AlertTitle>
            <AlertDescription>
              No module owns branches as records yet, so the API sends a null code and name for
              every one of them. The identifier is what is shown — it is the only true label
              available.
            </AlertDescription>
          </div>
        </Alert>
      ) : null}

      {expiring.length > 0 ? (
        <Alert tone="info">
          <Clock aria-hidden />
          <div className="space-y-1">
            <AlertTitle>Some of this access is time-boxed</AlertTitle>
            <AlertDescription>
              <ul className="space-y-0.5">
                {expiring.map((branch) =>
                  branch.accessExpiresAtUtc ? (
                    <li key={branch.id}>
                      {branchLabel(branch)} — cover ends{' '}
                      <span className="numeric tabular-nums">
                        {formatDateTime(branch.accessExpiresAtUtc)}
                      </span>
                    </li>
                  ) : null,
                )}
              </ul>
              A grant that lapses while this page is open leaves the branch on screen and stops the
              server answering for it. The refusal, not this list, is the authority.
            </AlertDescription>
          </div>
        </Alert>
      ) : null}
    </div>
  )
}

/** The expiry marker beside a branch on a card or a row. Absent where access is open-ended. */
export function BranchAccessBadge({ branch }: { branch: Branch }) {
  if (!branch.accessExpiresAtUtc) {
    return null
  }

  return (
    <Badge tone="warning" className="whitespace-normal">
      Cover ends {formatDateTime(branch.accessExpiresAtUtc)}
    </Badge>
  )
}

export interface BranchSelectProps {
  id: string
  label: string
  description?: string
  branches: Branch[]
  value: string | null
  onChange: (branchId: string) => void
  disabled?: boolean
}

/**
 * A branch chooser over the reachable list.
 *
 * It offers only what `/access/me/branches` returned. Free text was considered and rejected: a
 * mistyped identifier on the screen that opens a trading day would open the wrong branch's day,
 * and nothing in this application can close one again.
 */
export function BranchSelect({
  id,
  label,
  description,
  branches,
  value,
  onChange,
  disabled = false,
}: BranchSelectProps) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id}>{label}</Label>
      <Select value={value ?? undefined} onValueChange={onChange} disabled={disabled || branches.length === 0}>
        <SelectTrigger id={id} aria-describedby={description ? `${id}-description` : undefined}>
          <SelectValue placeholder={branches.length === 0 ? 'No branch available' : 'Select a branch…'} />
        </SelectTrigger>
        <SelectContent>
          {branches.map((branch) => (
            <SelectItem key={branch.id} value={branch.id}>
              {branchDescription(branch)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {description ? (
        <p id={`${id}-description`} className="text-xs text-muted">
          {description}
        </p>
      ) : null}
    </div>
  )
}
